import { Box } from '@mui/material';
import React from 'react';
import { themeColors } from '../JuvareTheme';
import { CardAction, CardContent, CardIcon } from './components';
/**
 * Card component that renders a layout with optional icon, content, and action areas.
 *
 * @component
 * @param {CardProps} props - The props for the Card component.
 * @param {string} [props.data-test] - Optional data-test attribute for testing.
 * @param {React.ReactNode} [props.icon] - Optional icon displayed at the start of the card.
 * @param {string} [props.iconColor] - Optional color for the icon.
 * @param {string} [props.contentTitle] - Title text to display in the content area.
 * @param {string | number} [props.contentBadge] - Badge displayed next to the title.
 * @param {React.ReactNode} [props.contentTitleBadge] - Inline badge component shown next to the title.
 * @param {React.ReactNode[]} [props.featureIcons] - Optional feature icons shown in the content area.
 * @param {() => void} [props.onContentClick] - Optional click handler for the content area.
 * @param {string} [props.contentAriaLabel] - ARIA label for accessibility on content.
 * @param {React.ReactNode} [props.actionIcon] - Optional action icon displayed on the right side.
 * @param {string} [props.actionAriaLabel] - ARIA label for accessibility on action.
 * @param {() => void} [props.onActionClick] - Optional click handler for the action icon.
 *
 * @returns {React.ReactElement} The rendered Card component.
 */
export const Card = (props) => {
    return (React.createElement(Box, { sx: {
            display: 'grid',
            gridTemplateColumns: props.icon ? '80px 1fr auto' : '1fr auto',
            backgroundColor: themeColors.surface.primary,
            border: 1,
            minHeight: 80,
            borderRadius: 1,
            borderColor: themeColors.border.main,
            '&:hover': {
                boxShadow: 4,
                transition: '150ms ease-in-out',
            },
            transition: '150ms ease-in-out',
        }, "data-test": props['data-test'] },
        props.icon ? React.createElement(CardIcon, { iconColor: props.iconColor, icon: props.icon }) : null,
        React.createElement(CardContent, { title: props.contentTitle, badge: props.contentBadge, inlineBadge: props.contentTitleBadge, featureIcons: props.featureIcons, onClick: props.onContentClick, ariaLabel: props.contentAriaLabel }),
        props.actionIcon ? (React.createElement(CardAction, { icon: props.actionIcon, ariaLabel: props.actionAriaLabel, onClick: props.onActionClick })) : null));
};
